/**
 * Calendar dates and instants are different values. These adapters require the
 * caller to identify whether a Date came from a picker or date-only storage;
 * midnight/noon is never used to guess whether an instant is a calendar date.
 */
import { ORGANIZATION_SETTINGS_KEYS } from '../types/organization-settings'

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const STORED_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

function utcDate(year: number, month: number, day: number, hour = 0): Date {
  // Date.UTC(0..99, ...) silently adds 1900. Explicit full-year assignment
  // keeps validation and round trips faithful to the supplied calendar year.
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  date.setUTCHours(hour, 0, 0, 0)
  return date
}

function dateKey(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const match = CALENDAR_DATE.exec(value)
  if (!match) return false
  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return false
  const date = utcDate(year, month, day)
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  )
}

/** Local Date objects returned by native/web date pickers carry local parts. */
export function calendarDateFromPicker(value: Date): string {
  if (!Number.isFinite(value.getTime())) return ''
  const key = dateKey(
    value.getFullYear(),
    value.getMonth() + 1,
    value.getDate(),
  )
  return isCalendarDate(key) ? key : ''
}

/**
 * Read an explicitly date-only API/database field. Prisma date columns may
 * arrive as UTC-midnight Date objects/ISO strings; legacy writes use UTC noon.
 * This adapter must not be used for meetings, audit timestamps, or timers.
 */
export function calendarDateFromStorage(
  value: Date | string | null | undefined,
): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string') {
    if (CALENDAR_DATE.test(value)) return isCalendarDate(value) ? value : ''
    if (!STORED_TIMESTAMP.test(value) || !isCalendarDate(value.slice(0, 10)))
      return ''
  }
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  const key = dateKey(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  )
  return isCalendarDate(key) ? key : ''
}

/** Bridge a calendar date into date-fns/native pickers without shifting its day. */
export function calendarDateToPicker(value: string): Date | null {
  if (!isCalendarDate(value)) return null
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7))
  const day = Number(value.slice(8, 10))
  const date = new Date(0)
  date.setFullYear(year, month - 1, day)
  date.setHours(12, 0, 0, 0)
  // A historical skipped civil day cannot be represented by a local Date.
  return calendarDateFromPicker(date) === value ? date : null
}

/** Retain the existing noon-UTC wire contract for date-only API fields. */
export function calendarDateToStorage(value: string): Date {
  if (!isCalendarDate(value)) throw new RangeError('Invalid calendar date')
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7))
  const day = Number(value.slice(8, 10))
  return utcDate(year, month, day, 12)
}

/** Calendar arithmetic independent of the device/server timezone and DST. */
export function addCalendarDays(value: string, days: number): string {
  if (!Number.isSafeInteger(days))
    throw new RangeError('Invalid calendar day offset')
  const date = calendarDateToStorage(value)
  date.setUTCDate(date.getUTCDate() + days)
  const result = calendarDateFromStorage(date)
  if (!result) throw new RangeError('Calendar date out of range')
  return result
}

/** Format the literal calendar day, never the device's interpretation of it. */
export function formatCalendarDate(
  value: string,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: 'UTC',
  }).format(calendarDateToStorage(value))
}

/** Week boundaries are calendar keys, including days skipped in a device zone. */
export function startOfCalendarWeek(value: string, weekStartsOn = 1): string {
  if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || weekStartsOn > 6)
    throw new RangeError('Invalid week start')
  const weekday = calendarDateToStorage(value).getUTCDay()
  return addCalendarDays(value, -((weekday - weekStartsOn + 7) % 7))
}

function validatedTimeZone(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate || /^[+-]/.test(candidate))
    return null
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: candidate,
    }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

/** Select a valid saved/business/device timezone, in caller-defined priority. */
export function resolveTimeZone(
  ...candidates: (string | null | undefined)[]
): string {
  for (const candidate of candidates) {
    const resolved = validatedTimeZone(candidate)
    if (resolved) return resolved
  }
  return 'UTC'
}

/** Business calculations use the workspace zone, including historical settings keys. */
export function resolveOrganizationTimeZone(settings: unknown): string {
  const value = settings as {
    timezone?: string
    timeZone?: string
    general?: { timezone?: string; timeZone?: string }
    [ORGANIZATION_SETTINGS_KEYS.GENERAL]?: {
      timezone?: string
      timeZone?: string
    }
  } | null
  const timeZone =
    value?.timezone ??
    value?.timeZone ??
    value?.[ORGANIZATION_SETTINGS_KEYS.GENERAL]?.timezone ??
    value?.[ORGANIZATION_SETTINGS_KEYS.GENERAL]?.timeZone ??
    value?.general?.timezone ??
    value?.general?.timeZone
  // Preserve the saved name: ICU versions canonicalize aliases differently,
  // and API calendar metadata must not rename a valid workspace preference.
  return typeof timeZone === 'string' && validatedTimeZone(timeZone)
    ? timeZone
    : 'UTC'
}

/** Determine the reporting day for an instant in an explicitly supplied zone. */
export function calendarDateInTimeZone(
  instant: Date,
  timeZone: string,
): string {
  if (!Number.isFinite(instant.getTime())) return ''
  // Unlike preference resolution, this low-level operation rejects invalid
  // zones: silently using UTC would corrupt the requested reporting scope.
  const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(entry => entry.type === type)?.value ?? ''
  const key = `${part('year').padStart(4, '0')}-${part('month')}-${part('day')}`
  return isCalendarDate(key) ? key : ''
}
